import React, { useState, useEffect } from 'react';
import WelcomeScreen from './components/WelcomeScreen';
import PoseList from './components/PoseList';
import AudioManager from './utils/AudioManager';
import './App.css';

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPose, setCurrentPose] = useState(null);
  const [loadingPose, setLoadingPose] = useState(null);
  const [selectedPose, setSelectedPose] = useState(null);
  const [audioManager] = useState(() => new AudioManager());

  useEffect(() => {
    // Load saved pose from cookie on app start
    const savedPose = localStorage.getItem('lastPlayedPose');
    if (savedPose) {
      setCurrentPose(parseInt(savedPose));
    }
  }, []);

  const handleInitialize = async () => {
    try {
      setIsLoading(true);
      await audioManager.initialize();
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      alert('Failed to initialize audio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePoseSelect = async (poseNumber) => {
    if (!isInitialized) return;
    
    // Immediately show visual feedback
    setSelectedPose(poseNumber);
    
    try {
      // Only show loading if audio needs to be loaded
      const needsLoading = !audioManager.players[poseNumber];
      if (needsLoading) {
        setLoadingPose(poseNumber);
      }
      
      await audioManager.playPose(poseNumber);
      setCurrentPose(poseNumber);
      // Save to localStorage (cookie alternative for persistence)
      localStorage.setItem('lastPlayedPose', poseNumber.toString());
    } catch (error) {
      console.error('Failed to play pose:', error);
      alert(`Failed to load audio for pose ${poseNumber}. Please try again.`);
    } finally {
      setLoadingPose(null);
      setSelectedPose(null); // Clear selection state
    }
  };

  const handleStop = () => {
    audioManager.stop();
    setCurrentPose(null);
    localStorage.removeItem('lastPlayedPose');
  };

  if (!isInitialized) {
    return <WelcomeScreen onInitialize={handleInitialize} isLoading={isLoading} />;
  }

  return (
    <div className="app">
      <PoseList 
        currentPose={currentPose}
        loadingPose={loadingPose}
        selectedPose={selectedPose}
        onPoseSelect={handlePoseSelect}
      />
      <div className="stop-button-container">
        <button 
          className="stop-button"
          onClick={handleStop}
          disabled={!currentPose}
        >
          Stop Audio
        </button>
      </div>
      
      <div className="footer-message">
        <p>
          Think of this as a starting point: a simple demo now, but designed to grow with more loops and poses, and intuitive enough to be run by the teacher during class—whether on a smartphone or tablet, connected to the studio's sound system via Bluetooth. If this sparks ideas, I'd love to hear from you: drop me a line at{' '}
          <a 
            href="mailto:peter.ducharme@gmail.com?subject=Pose DJ"
            className="email-link"
          >
            peter.ducharme@gmail.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default App;
